import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BarChart3, Check, Clock, Globe, PanelsTopLeft, ShieldCheck, Sparkles } from "lucide-react";
import { useState, type ReactNode } from "react";
import { Link, Navigate, useNavigate } from "react-router";

import { useOrganization } from "@/auth/organization";
import { Button, ErrorAlert, ErrorState, FullPageSpinner, Badge, useToast } from "@/components/ui";
import { portal } from "@/lib/api";
import { formatDate, formatDayMonth } from "@/lib/format";
import { primarySite, useOrganizationDomains } from "@/lib/queries";

const pill = "inline-flex shrink-0 items-center rounded-full px-4 py-[9px] text-[13px] font-semibold";

/** Figma "Billing — Cancel Growth plan · review effects (BIL-02)". */
export function CancelPlanPage() {
  const { orgId, can, permissionsLoading } = useOrganization();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const sub = useQuery({ queryKey: ["subscription", orgId], queryFn: () => portal.subscription(orgId) });
  const domains = useOrganizationDomains();
  const [understood, setUnderstood] = useState(false);
  const toast = useToast();
  const cancel = useMutation({
    mutationFn: () => portal.changePlan(orgId, "free"),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["subscription", orgId] });
      await qc.invalidateQueries({ queryKey: ["overview", orgId] });
      toast.show({ tone: "success", title: "Plan cancellation scheduled" });
      navigate("/billing", { replace: true });
    },
  });

  if (permissionsLoading) return <FullPageSpinner />;
  if (!can("billing:manage")) return <Navigate to="/billing" replace />;
  if (sub.isPending || domains.isPending) return <FullPageSpinner />;
  if (sub.isError) return <ErrorState error={sub.error} onRetry={() => void sub.refetch()} />;
  const { account } = sub.data;
  const plan = account.plan;
  if (plan.monthly_price_cents === 0 || account.scheduled_change?.plan.key === "free")
    return <Navigate to="/billing" replace />;

  const ends = formatDate(account.current_period_end);
  const endsShort = account.current_period_end ? formatDayMonth(account.current_period_end) : ends;
  const site = primarySite(domains.data?.sites ?? []);
  const connected = (domains.data?.domains ?? [])
    .filter((d) => d.domain.status === "active")
    .map((d) => d.domain.hostname);
  const named = connected.length ? join(connected) : null;

  return (
    <div className="flex flex-col gap-6">
      <p className="text-[13px] whitespace-pre text-fg-subtle">
        <Link to="/billing" className="hover:text-fg">
          Billing
        </Link>
        {"  /  "}Cancel plan
      </p>
      <div>
        <h1 className="text-[26px] font-bold text-fg">Cancel your {plan.name} plan?</h1>
        <p className="mt-1 text-sm text-fg-subtle">
          Nothing changes until you confirm. Here’s exactly what happens and when.
        </p>
      </div>

      <section className="flex w-full max-w-[780px] flex-col gap-4 rounded-[18px] border border-border bg-surface px-[22px] py-5">
        <div className="flex items-center gap-3 rounded-xl bg-brand-50 px-4 py-3.5">
          <Clock className="size-[18px] shrink-0 text-fg-muted" aria-hidden />
          <div className="min-w-0">
            <p className="text-[15px] font-semibold text-fg">Your plan ends on {ends}</p>
            <p className="text-[12.5px] leading-normal text-fg-muted">
              You keep {plan.name} until then. No refund for the current{" "}
              {account.billing_period === "annual" ? "year" : "month"}, and nothing more is charged for the
              plan.
            </p>
          </div>
        </div>

        <p className="text-[11.5px] font-semibold tracking-[0.05em] text-fg-subtle uppercase">
          What happens to each service
        </p>
        {site && (
          <Effect
            icon={<PanelsTopLeft className="size-4" aria-hidden />}
            title="Website"
            status={
              named ? <Badge tone="warning">Changes</Badge> : <Badge tone="success">Not affected</Badge>
            }
          >
            {named
              ? `After ${endsShort} your site stays published on ${site.hostname}. ${named} stop${connected.length > 1 ? "" : "s"} showing it until you upgrade again.`
              : `Your site stays published on ${site.hostname}.`}
          </Effect>
        )}
        <Effect
          icon={<Globe className="size-4" aria-hidden />}
          title="Domains"
          status={<Badge tone="success">Not affected</Badge>}
        >
          {named
            ? `${named} stay registered to you wherever you bought ${connected.length > 1 ? "them" : "it"}; reconnect any time you upgrade.`
            : "Free includes the CortexGrip address only; you can connect your own domain again when you upgrade."}
        </Effect>
        <Effect
          icon={<Sparkles className="size-4" aria-hidden />}
          title="Weavex edits"
          status={<Badge tone="warning">Ends {endsShort}</Badge>}
        >
          You move to 3 Weavex edits a month. Your pages stay exactly as they are.
        </Effect>
        <Effect
          icon={<BarChart3 className="size-4" aria-hidden />}
          title="Analytics"
          status={<Badge tone="warning">Changes</Badge>}
        >
          Only the last 7 days are shown on Free. Nothing is deleted, so the full history returns if you
          upgrade.
        </Effect>
        <Effect
          icon={<ShieldCheck className="size-4" aria-hidden />}
          title="Growth Intelligence"
          status={<Badge tone="warning">Ends {endsShort}</Badge>}
        >
          Insights and alerts stop. Saved items are kept if you come back.
        </Effect>

        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-brand-100 bg-[#fef3f0] px-4 py-3.5">
          <div className="min-w-0 flex-1">
            <p className="text-[13.5px] font-semibold text-fg">
              Changed your mind? You can reactivate any time before {endsShort}.
            </p>
            <p className="text-[12.5px] text-fg-muted">
              Or compare plans to move to a different one instead.
            </p>
          </div>
          <Link
            to="/billing/plans"
            className={`${pill} border border-border bg-surface text-fg hover:bg-muted`}
          >
            Compare plans
          </Link>
        </div>

        <label className="flex cursor-pointer items-start gap-2.5 rounded-xl border border-border px-3.5 py-3">
          <input
            type="checkbox"
            className="peer sr-only"
            checked={understood}
            onChange={(e) => setUnderstood(e.target.checked)}
          />
          <span
            aria-hidden
            className="mt-0.5 flex size-[18px] shrink-0 items-center justify-center rounded-[5px] border border-[#d4cac4] text-white peer-checked:border-[#12a97e] peer-checked:bg-[#12a97e] peer-focus-visible:ring-2 peer-focus-visible:ring-[#12a97e]/40"
          >
            <Check className="size-3" strokeWidth={3} />
          </span>
          <span className="text-[13px] leading-[1.55] text-fg">
            {named
              ? `I understand ${named} will stop showing my website after ${endsShort}.`
              : `I understand my ${plan.name} features end after ${endsShort}.`}
          </span>
        </label>

        <ErrorAlert error={cancel.error} />
        <div className="h-px bg-border" />
        <div className="flex justify-end gap-2.5">
          <Link to="/billing" className={`${pill} border border-border bg-surface text-fg hover:bg-muted`}>
            Keep my plan
          </Link>
          <Button
            unstyled
            loading={cancel.isPending}
            disabledReason={understood ? undefined : "Tick the box above to confirm you understand."}
            onClick={() => cancel.mutate()}
            className={`${pill} bg-brand-500 text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50`}
          >
            {cancel.isPending ? "Cancelling…" : "Cancel at end of term"}
          </Button>
        </div>
      </section>
    </div>
  );
}

function Effect({
  icon,
  title,
  status,
  children,
}: {
  icon: ReactNode;
  title: string;
  status: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="flex items-start gap-3 py-1">
      <span className="mt-px shrink-0 text-fg-muted">{icon}</span>
      <div className="min-w-0 flex-1">
        <p className="text-[13.5px] font-semibold text-fg">{title}</p>
        <p className="mt-0.5 text-[12.5px] leading-normal text-fg-muted">{children}</p>
      </div>
      {status}
    </div>
  );
}

const join = (items: string[]): string =>
  items.length <= 1 ? (items[0] ?? "") : `${items.slice(0, -1).join(", ")} and ${items.at(-1)}`;
